import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, User, LogOut } from 'lucide-react';
import Logo from './Logo';
import { BRAND_NAME } from '../utils/brand';
import { useAuthUser } from '../hooks/useAuthUser';
import { useReferralCta } from '../hooks/useReferralCta';
import { PORTAL_DASHBOARD_URL, PORTAL_LOGOUT_URL, PORTAL_CLINIC_SIGNIN_URL } from '../utils/ctaUrl';

const NAV = [
  // { label: 'Weight Loss',  to: '/weight-loss' },
  // { label: 'How it works', to: '/how-it-works' },
  // { label: 'Packages',     to: '/packages' },
  // { label: 'Features',     to: '/features' },
  // { label: 'Why us',       to: '/why-us' },
  // { label: 'About',        to: '/about' },
  // { label: 'FAQ',          to: '/faq' },
  // { label: 'Contact',      to: '/contact' }
];

function AccountMenu({ user }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (!ref.current?.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  const displayName = user.firstName || 'My account';
  const initial = (user.firstName || 'M').slice(0, 1).toUpperCase();

  // Send the portal's /logout back here when it's done.
  const here = typeof window !== 'undefined' ? window.location.origin : '';
  const logoutHref = `${PORTAL_LOGOUT_URL}?next=${encodeURIComponent(here || '/')}`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-white/25 hover:border-white/50 transition-colors"
      >
        <span className="w-7 h-7 rounded-full bg-brand-600 text-white text-xs font-semibold flex items-center justify-center">
          {initial}
        </span>
        <span className="text-sm font-medium text-white max-w-[10rem] truncate">
          {displayName}
        </span>
        <ChevronDown
          size={14}
          className={`text-white/70 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-60 bg-white rounded-xl border border-ink-200 shadow-card overflow-hidden z-50"
        >
          <div className="px-4 py-3 border-b border-ink-100">
            <div className="text-[10px] uppercase tracking-widest text-ink-500 font-semibold">Signed in</div>
            <div className="mt-0.5 text-sm font-semibold text-ink-900 truncate">{displayName}</div>
          </div>
          <a
            href={PORTAL_DASHBOARD_URL}
            role="menuitem"
            className="flex items-center gap-2.5 px-4 py-3 text-sm text-ink-900 hover:bg-ink-50 transition-colors"
          >
            <User size={15} className="text-ink-500" /> My account
          </a>
          <a
            href={logoutHref}
            role="menuitem"
            className="flex items-center gap-2.5 px-4 py-3 text-sm text-rose-700 hover:bg-rose-50 transition-colors border-t border-ink-100"
          >
            <LogOut size={15} /> Log out
          </a>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const user = useAuthUser();
  const authed = !!user;
  const { ctaUrl, signInUrl } = useReferralCta();

  useEffect(() => {
    // Hide on scroll-down past the header, reveal on scroll-up or near top.
    // 8px shadow threshold is unchanged; `hidden` toggles the transform.
    let lastY = window.scrollY;
    let ticking = false;
    const REVEAL_ZONE = 80; // near top / small movements keep the bar visible
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 8);
        if (y <= REVEAL_ZONE) {
          setHidden(false);
        } else if (y > lastY + 4) {
          setHidden(true);
        } else if (y < lastY - 4) {
          setHidden(false);
        }
        lastY = y;
        ticking = false;
      });
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Always reveal when the mobile menu is open — otherwise the panel dangles.
  useEffect(() => { if (open) setHidden(false); }, [open]);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const here = typeof window !== 'undefined' ? window.location.origin : '';
  const mobileLogoutHref = `${PORTAL_LOGOUT_URL}?next=${encodeURIComponent(here || '/')}`;

  return (
    <header
      className={[
        'sticky top-0 z-40 text-white isolate',
        'transition-transform duration-300 ease-out will-change-transform',
        hidden ? '-translate-y-full' : 'translate-y-0',
        scrolled ? 'shadow-soft' : '',
      ].join(' ')}
      style={{
        // Match the top slice of the hero gradient exactly so the navbar
        // feels like a continuation of the hero surface rather than its own
        // overlay panel.
        backgroundImage: `linear-gradient(180deg, rgba(2,33,63,0.30) 0%, rgba(2,33,63,0.45) 100%), url("/Web Section 1 - Hero Background.png")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="container-x flex items-center justify-between h-16">
        <Link to="/" className="shrink-0" aria-label={`${BRAND_NAME} home`}>
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 text-sm">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                [
                  'transition-colors hover:text-brand-300',
                  isActive ? 'text-brand-300 font-semibold' : 'text-white font-medium',
                ].join(' ')
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          {authed ? (
            <AccountMenu user={user} />
          ) : (
            <>
              {/* Client 2026-09-10: two distinct entry points instead of the
                  generic "Sign in / Get started" pair. Members go to the
                  patient login, partners (clinic / staff / admin) go to
                  the portal login. */}
              <a
                href={signInUrl}
                className="text-sm font-medium px-4 py-2 rounded-full text-white hover:bg-white/10 transition-colors"
              >
                Member Log In
              </a>
              <a
                href={PORTAL_CLINIC_SIGNIN_URL}
                className="text-sm font-semibold px-4 py-2 rounded-full bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-card"
              >
                For Partners
              </a>
            </>
          )}
        </div>

        <button
          className="lg:hidden p-2 rounded-lg transition-colors text-white hover:bg-white/10"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-white/10 text-white"
          style={{
            // Same hero surface; slightly deeper overlay only because the
            // dropdown sits over arbitrary page content underneath.
            backgroundImage: `linear-gradient(180deg, rgba(2,33,63,0.55), rgba(2,33,63,0.70)), url("/Web Section 1 - Hero Background.png")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
          }}
        >
          <div className="container-x py-4 flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `py-2.5 hover:text-brand-300 ${isActive ? 'text-brand-300 font-semibold' : 'text-white font-medium'}`
                }
              >
                {n.label}
              </NavLink>
            ))}

            <div className="pt-3 mt-2 border-t border-white/10 flex flex-col gap-2">
              {authed ? (
                <>
                  <div className="flex items-center gap-2 px-1 pb-1">
                    <span className="w-7 h-7 rounded-full bg-brand-600 text-white text-xs font-semibold flex items-center justify-center">
                      {(user.firstName || 'M').slice(0, 1).toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-white truncate">
                      {user.firstName || 'My account'}
                    </span>
                  </div>
                  <a
                    href={PORTAL_DASHBOARD_URL}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
                  >
                    <User size={16} /> My account
                  </a>
                  <a
                    href={mobileLogoutHref}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-rose-200 border border-rose-300/40 hover:bg-rose-500/10 transition-colors"
                  >
                    <LogOut size={16} /> Log out
                  </a>
                </>
              ) : (
                <>
                  {/* Mobile menu — same Member / Partner split. */}
                  <a
                    href={signInUrl}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border border-white/25 text-white hover:bg-white/10 transition-colors flex-1"
                  >
                    Member Log In
                  </a>
                  <a
                    href={PORTAL_CLINIC_SIGNIN_URL}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors flex-1"
                  >
                    For Partners
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
